import { tr } from 'i18n';
import { useTranslation as useInterfaceLanguage } from 'react-i18next';
import IntegrationsPanel from 'components/Erp/IntegrationsPanel';
import { hasAccess } from 'utils/access';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import styles from './ReportCard.module.scss';
import { Layout } from 'components';
import Notification from 'utils/Notifications';
import moment from 'moment';
import { ArrowDownToLine, Truck, History, BarChart3, Users, RefreshCw, ShieldCheck } from 'lucide-react';

// Hooks
import useGeolocation from './hooks/useGeolocation';
import useUserRoles from './hooks/useUserRoles';
import useCamera from './hooks/useCamera';
import useLoads from './hooks/useLoads';

// Components
import ScanDataContext from './components/ScanDataContext';
import ScannerOverlay from './components/ScannerOverlay';
import ScanButton from './components/ScanButton';
import OperatorDriverPanel from './components/OperatorDriverPanel';
import InLoadingTableView from './components/InLoadingTableView';
import InRouteTableView from './components/InRouteTableView';
import CompletedLoadsTableView from './components/CompletedLoadsTableView';
import HistoryModalView from './components/HistoryModalView';
import AnalyticsSection from './components/AnalyticsSection';
import StatusModals from './components/StatusModals';
import DriverCabinet from './components/DriverCabinet';
import { Link } from 'react-router-dom';
import BaseDriversPanel from './components/BaseDriversPanel';
import OperatorScanModal from './components/OperatorScanModal';

// Services
import { getDriverByUid, postLoadByDriverUid } from 'service/TabelService';
import { getErpErrorMessage } from 'service/ErpService';
import { getDriverLastLoadA, putLoadMarkB, postLoadMarkBGuard } from 'service/DriverService';
import { markExit } from 'service/LoadService';

// Utils
import { getCurrentGeoPosition } from './utils/getGeoPosition';
import { formatBranchDistances } from './utils/geo';
import { assertOperatorReceiptLocation, checkOperatorReceiptLocation, getOperatorReceiptError, OperatorReceiptError, unavailableOperatorLocation } from './utils/operatorReceiptGeo';

const isQueueSearchActive = (loads, loading, route, history) => Boolean((loading && loads.inLoadingSearch?.trim()) || (route && loads.inRouteSearch?.trim()) || (history && loads.completedSearch?.trim()));

const ReportCard = () => {
  useInterfaceLanguage();
  // ─── State ───────────────────────────────────────────────
  const [refresh, setRefresh] = useState(false);
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' });
  const [scanData, setScanData] = useState(null);
  const [showQr, setShowQr] = useState(false);

  // Scanner steps & photo
  const [scanningStep, setScanningStep] = useState('scan');
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [photoBlob, setPhotoBlob] = useState(null);
  const [pendingLoadData, setPendingLoadData] = useState(null);

  // Guard
  const [driverUid, setDriverUid] = useState(null);
  const [driverData, setDriverData] = useState(null);
  const [loadingDriver, setLoadingDriver] = useState(false);
  const [errorDriver, setErrorDriver] = useState(null);
  const [, setAllowBtnLoading] = useState(false);
  const [allowBtnError, setAllowBtnError] = useState(null);
  const [guardSuccess, setGuardSuccess] = useState(false);

  // Operator scan state (unified for all 3 types)
  const [topBaseScannedDriver, setTopBaseScannedDriver] = useState(null);
  const [topBaseLastLoadA, setTopBaseLastLoadA] = useState(null);
  const [topBaseScanLoading, setTopBaseScanLoading] = useState(false);
  const [topBaseScanError, setTopBaseScanError] = useState(null);
  const [topBaseConfirmLoading, setTopBaseConfirmLoading] = useState(false);
  const [topBaseConfirmError, setTopBaseConfirmError] = useState(null);
  const [topBaseSuccess, setTopBaseSuccess] = useState(false);

  const [chppScannedDriver, setChppScannedDriver] = useState(null);
  const [chppLastLoadA, setChppLastLoadA] = useState(null);
  const [chppScanLoading, setChppScanLoading] = useState(false);
  const [chppScanError, setChppScanError] = useState(null);
  const [chppConfirmLoading, setChppConfirmLoading] = useState(false);
  const [chppConfirmError, setChppConfirmError] = useState(null);
  const [chppSuccess, setChppSuccess] = useState(false);

  const [privatePointScannedDriver, setPrivatePointScannedDriver] = useState(null);
  const [privatePointLastLoadA, setPrivatePointLastLoadA] = useState(null);
  const [privatePointScanLoading, setPrivatePointScanLoading] = useState(false);
  const [privatePointScanError, setPrivatePointScanError] = useState(null);
  const [privatePointConfirmLoading, setPrivatePointConfirmLoading] = useState(false);
  const [privatePointConfirmError, setPrivatePointConfirmError] = useState(null);
  const [privatePointSuccess, setPrivatePointSuccess] = useState(false);

  // Modals
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successModalData, setSuccessModalData] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalData, setErrorModalData] = useState(null);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [warningModalData, setWarningModalData] = useState(null);

  // Новый модальный оператор-сканер
  const [showOperatorModal, setShowOperatorModal] = useState(false);
  const operatorReceiptPending = useRef(false);
  const [operatorGeoChecking, setOperatorGeoChecking] = useState(false);

  // Раскрывающиеся секции
  const [activeTab, setActiveTab] = useState('inLoading');
  const [showInLoading, setShowInLoading] = useState(true);
  const [showInRoute, setShowInRoute] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);

  const selectTab = (key) => {
    const next = key;
    setActiveTab(next);
    setShowInLoading(next === 'inLoading');
    setShowInRoute(next === 'inRoute');
    setShowHistory(next === 'history');
    setShowAnalytics(next === 'analytics');
    // 'drivers' tab has no separate boolean — controlled by activeTab directly
  };

  // ─── Hooks ───────────────────────────────────────────────
  const { user, isGuard, isTopBaseOperator, isChppOperator, isPrivatePointOperator, isDriverChecked, roleLoading, roleError, retryRoles } = useUserRoles();
  const operational = !!(isGuard || isTopBaseOperator || isChppOperator || isPrivatePointOperator);
  const { userGeoLocation, setUserGeoLocation } = useGeolocation(operational);

  // Для операторов баз — по умолчанию показывать вкладку "В пути"
  useEffect(() => {
    if (!roleLoading && (isTopBaseOperator || isChppOperator || isPrivatePointOperator)) {
      setActiveTab('inRoute');
      setShowInLoading(false);
      setShowInRoute(true);
    }
  }, [roleLoading, isTopBaseOperator, isChppOperator, isPrivatePointOperator]);

  const loads = useLoads({
    isGuard,
    isTopBaseOperator,
    isChppOperator,
    isPrivatePointOperator,
    user,
    refresh,
    enabled: !roleLoading && !roleError && !isDriverChecked,
    onNotify: setNotify,
    showInLoading,
    showInRoute,
    showHistory,
    showAnalytics,
  });

  // ─── Helpers ─────────────────────────────────────────────
  /** Сбросить все operator scan states */
  const resetAllOperatorState = useCallback(() => {
    setTopBaseScannedDriver(null);
    setTopBaseLastLoadA(null);
    setChppScannedDriver(null);
    setChppLastLoadA(null);
    setPrivatePointScannedDriver(null);
    setPrivatePointLastLoadA(null);
    setCapturedPhoto(null);
    setPhotoBlob(null);
    setPendingLoadData(null);
  }, []);

  /** Показать модалку успеха с задержкой */
  const showSuccess = useCallback((driverDataObj, actionType = 'entry') => {
    const modalData = {
      fio: driverDataObj?.employee_name || '-',
      phone: driverDataObj?.employee_phone || '-',
      vehicleNumber: driverDataObj?.vehicle_number || '-',
      base: driverDataObj?.top_base_name || driverDataObj?.chpp_name || driverDataObj?.private_point_name || '-',
      actionType,
    };
    setTimeout(() => {
      setSuccessModalData(modalData);
      setShowSuccessModal(true);
    }, 300);
  }, []);

  /** Получить гео (из state или свежую) */
  const ensureGeo = useCallback(async ({ fresh = false } = {}) => {
    if (!fresh && userGeoLocation && typeof userGeoLocation === 'string' && userGeoLocation.trim()) {
      return userGeoLocation;
    }
    const geo = await getCurrentGeoPosition();
    setUserGeoLocation(geo);
    return geo;
  }, [userGeoLocation, setUserGeoLocation]);

  const ensureOperatorReceiptGeo = useCallback(async (lastLoadA) => {
    let geo;
    try { geo = await ensureGeo({ fresh: true }); }
    catch { throw new OperatorReceiptError(unavailableOperatorLocation()); }
    assertOperatorReceiptLocation(geo, lastLoadA);
    return geo;
  }, [ensureGeo]);

  const recheckOperatorLocation = useCallback(async () => {
    if (operatorGeoChecking || operatorReceiptPending.current) return;
    setOperatorGeoChecking(true);
    setShowWarningModal(false);
    try { await ensureGeo({ fresh: true }); }
    catch { setNotify({ isOpen: true, message: unavailableOperatorLocation(), type: 'warning' }); }
    finally { setOperatorGeoChecking(false); }
  }, [ensureGeo, operatorGeoChecking]);

  const is504 = useCallback((e) => {
    const status = e?.response?.status || e?.status;
    return !e?.response || status === 504 || (e?.message && (e.message.includes('504') || e.message.toLowerCase().includes('gateway')));
  }, []);

  // ─── Close scanner ──────────────────────────────────────
  const handleCloseScanner = useCallback(() => {
    setShowQr(false);
    setScanningStep('scan');
    resetAllOperatorState();
  }, [resetAllOperatorState]);

  // ─── QR Scanned handler ─────────────────────────────────
  const handleQRCodeScanned = useCallback(async (qrData) => {
    // --- Guard ---
    if (isGuard) {
      setShowQr(false);
      setDriverUid(qrData);
      setLoadingDriver(true);
      setErrorDriver(null);
      try {
        const res = await getDriverByUid(qrData);
        const driverStatus = res.data?.status;
        const isDriverFree = !driverStatus || driverStatus === 'Свободен';
        const lastLoadA = res.data?.last_load_a;
        const lastCompletedLoad = res.data?.last_load_b;
        const hasUncompletedTrip = lastLoadA && (!lastCompletedLoad || lastLoadA.id > lastCompletedLoad.id) && !isDriverFree;

        // Надёжный парсинг времени (формат "DD.MM.YYYY HH:mm" или ISO)
        const parseLoadTime = (timeStr) => {
          if (!timeStr) return null;
          let m = moment(timeStr, 'DD.MM.YYYY HH:mm', true);
          if (m.isValid()) return m;
          m = moment(timeStr, moment.ISO_8601, true);
          if (m.isValid()) return m;
          m = moment(timeStr); // нестрогий режим как fallback
          if (m.isValid()) return m;
          return null;
        };

        if (hasUncompletedTrip) {
          const baseName = res.data?.top_base_name || res.data?.chpp_name || res.data?.private_point_name || '-';

          // Проверяем статус по обоим полям: driver.status и load.status
          const loadStatus = lastLoadA?.status;
          const isInLoading = driverStatus === 'В погрузке' || loadStatus === 'В погрузке';
          const isInRoute   = driverStatus === 'В пути'    || loadStatus === 'В пути';

          if (isInLoading) {
            // Водитель ещё в базе — проверяем 40 мин с въезда (time_a)
            const entryTime = parseLoadTime(lastLoadA.time_a || lastLoadA.note_a_datetime || lastLoadA.created_at);
            const minutesAgo = entryTime ? moment().diff(entryTime, 'minutes') : null;

            if (minutesAgo === null) {
              // Не удалось распарсить время — всё равно помечаем выезд
              try {
                const geoToUse = await ensureGeo();
                await markExit(lastLoadA.id, { geo_a_exit: geoToUse });
                setNotify({ isOpen: true, message: tr("Выезд отмечен"), type: 'success' });
                setRefresh(r => !r);
                setGuardSuccess(true);
                showSuccess(res.data, 'guard_exit');
              } catch (exitErr) {
                setErrorModalData({ reason: getErpErrorMessage(exitErr, tr("Ошибка при отметке выезда")), fio: res.data?.employee_name || '-', phone: res.data?.employee_phone || '-', vehicleNumber: res.data?.vehicle_number || '-', base: baseName });
                setShowErrorModal(true);
              }
              return;
            }

            if (minutesAgo >= 40) {
              // ≥ 40 мин — отмечаем выезд
              try {
                const geoToUse = await ensureGeo();
                await markExit(lastLoadA.id, { geo_a_exit: geoToUse });
                setNotify({ isOpen: true, message: tr("Выезд отмечен"), type: 'success' });
                setRefresh(r => !r);
                setGuardSuccess(true);
                showSuccess(res.data, 'guard_exit');
              } catch (exitErr) {
                setErrorModalData({ reason: getErpErrorMessage(exitErr, tr("Ошибка при отметке выезда")), fio: res.data?.employee_name || '-', phone: res.data?.employee_phone || '-', vehicleNumber: res.data?.vehicle_number || '-', base: baseName });
                setShowErrorModal(true);
              }
            } else {
              // < 40 мин — сообщаем, сколько осталось
              setLoadingDriver(false);
              setWarningModalData({
                title: tr("Водитель уже отмечен при въезде"),
                message: tr("Водитель въехал в базу {{value0}} мин. назад. Выезд можно отметить через {{value1}} мин.", { value0: minutesAgo, value1: 40 - minutesAgo }),
                distance: baseName,
                requirement: tr("Минимальное время нахождения в базе — 40 минут"),
              });
              setShowWarningModal(true);
            }
            return;
          }

          if (isInRoute) {
            // Водитель уже выехал — проверяем 40 мин с выезда (time_a_exit)
            const exitMoment = parseLoadTime(lastLoadA.time_a_exit);
            const minutesSinceExit = exitMoment ? moment().diff(exitMoment, 'minutes') : null;
            setLoadingDriver(false);
            if (minutesSinceExit !== null && minutesSinceExit < 40) {
              setWarningModalData({
                title: tr("Водитель уже в пути"),
                message: tr("Водитель выехал из базы {{value0}} мин. назад и ещё в пути.", { value0: minutesSinceExit }),
                distance: baseName,
                requirement: tr("Дождитесь завершения текущей доставки"),
              });
              setShowWarningModal(true);
            } else {
              setErrorModalData({
                reason: tr("ВОДИТЕЛЬ НЕ ЗАВЕРШИЛ ПРЕЖНЮЮ ПОГРУЗКУ"),
                fio: res.data?.employee_name || '-',
                phone: res.data?.employee_phone || '-',
                vehicleNumber: res.data?.vehicle_number || '-',
                base: baseName,
              });
              setShowErrorModal(true);
            }
            return;
          }
        }
        setDriverData(res.data);
      } catch (e) {
        setErrorDriver(tr("Ошибка загрузки данных водителя"));
        setDriverData(null);
      } finally {
        setLoadingDriver(false);
      }
      return;
    }

    // --- Operator scan helper ---
    const handleOperatorScan = async (setLoading, setError, setDriver, setLastLoad, baseNameKey, noLoadMsg) => {
      setLoading(true);
      setError(null);
      try {
        const res = await getDriverLastLoadA(qrData);
        setDriver(res.data || null);
        setLastLoad(res.data?.last_load_a || null);

        const lastLoadA = res.data?.last_load_a;
        const lastLoadB = res.data?.last_load_b;
        const baseName = res.data?.[baseNameKey] || '-';

        if (lastLoadA) {
          setPendingLoadData({ loadId: lastLoadA.id, lastLoadA, driverData: res.data });
          setScanningStep('photo');
          return;
        }

        // Нет last_load_a
        const closeAndReset = () => { setShowQr(false); setScanningStep('scan'); };

        if (lastLoadB?.time_b) {
          const minutesSince = moment().diff(moment(lastLoadB.time_b, 'DD.MM.YYYY HH:mm'), 'minutes');
          if (minutesSince >= 60) {
            closeAndReset();
            setErrorModalData({ reason: noLoadMsg, fio: res.data?.employee_name || '-', phone: res.data?.employee_phone || '-', vehicleNumber: res.data?.vehicle_number || '-', base: baseName });
            setShowErrorModal(true);
          } else {
            closeAndReset();
            setWarningModalData({ title: tr("Внимание"), message: tr("Водитель уже был отмечен"), distance: baseName, requirement: tr("Нет активной погрузки для этого водителя") });
            setShowWarningModal(true);
          }
          return;
        }

        if (lastLoadB && !lastLoadB.time_b) {
          closeAndReset();
          setWarningModalData({ title: tr("Внимание"), message: tr("Водитель уже был отмечен"), distance: baseName, requirement: tr("Нет активной погрузки для этого водителя") });
          setShowWarningModal(true);
          return;
        }

        // Нет ни A ни B
        closeAndReset();
        setErrorModalData({ reason: noLoadMsg, fio: res.data?.employee_name || '-', phone: res.data?.employee_phone || '-', vehicleNumber: res.data?.vehicle_number || '-', base: baseName });
        setShowErrorModal(true);
      } catch (e) {
        setError(tr("Ошибка загрузки данных водителя или загрузки"));
        setDriver(null);
        setLastLoad(null);
        setShowQr(false);
        setScanningStep('scan');
      } finally {
        setLoading(false);
      }
    };

    if (isTopBaseOperator) {
      await handleOperatorScan(setTopBaseScanLoading, setTopBaseScanError, setTopBaseScannedDriver, setTopBaseLastLoadA, 'top_base_name', tr("Нет погрузки для вашей Базы"));
    } else if (isChppOperator) {
      await handleOperatorScan(setChppScanLoading, setChppScanError, setChppScannedDriver, setChppLastLoadA, 'chpp_name', tr("Нет погрузки для вашей ТЭЦ"));
    } else if (isPrivatePointOperator) {
      await handleOperatorScan(setPrivatePointScanLoading, setPrivatePointScanError, setPrivatePointScannedDriver, setPrivatePointLastLoadA, 'private_point_name', tr("Нет погрузки для вашего соц.объекта или МСУ"));
    }
  }, [isGuard, isTopBaseOperator, isChppOperator, isPrivatePointOperator, ensureGeo, showSuccess]);

  // ─── Camera hook ─────────────────────────────────────────
  const camera = useCamera({
    showQr,
    onNotify: setNotify,
    onQRCodeScanned: handleQRCodeScanned,
  });

  // ─── capturePhoto (автоматически отправляет) ────────────
  const capturePhoto = useCallback(async (useSaved = false) => {
    if (!pendingLoadData || operatorReceiptPending.current) return;
    let blob = useSaved === true ? photoBlob : null;
    if (!blob) {
      if (!camera.videoRef.current || !camera.canvasRef.current) return;
      const video = camera.videoRef.current;
      const canvas = camera.canvasRef.current;
      const context = canvas.getContext('2d');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      setCapturedPhoto(canvas.toDataURL('image/jpeg', 0.8));
      blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.8));
      if (!blob) { setNotify({ isOpen: true, message: tr("Не удалось получить фото с камеры"), type: 'error' }); return; }
      setPhotoBlob(blob);
    }
    setScanningStep('sending');
    operatorReceiptPending.current = true;
    if (isTopBaseOperator) setTopBaseConfirmError(null);
    else if (isChppOperator) setChppConfirmError(null);
    else if (isPrivatePointOperator) setPrivatePointConfirmError(null);

    const capturedLoadId = pendingLoadData.loadId;
    const capturedDriverData = pendingLoadData.driverData;
    let formData = null;

    const onSubmitSuccess = () => {
      setNotify({ isOpen: true, message: tr("Готово!"), type: 'success' });
      setRefresh(r => !r);
      setShowQr(false);
      setScanningStep('scan');
      resetAllOperatorState();
      if (isTopBaseOperator) setTopBaseSuccess(true);
      else if (isChppOperator) setChppSuccess(true);
      else if (isPrivatePointOperator) setPrivatePointSuccess(true);
      showSuccess(capturedDriverData, 'exit');
    };

    try {
      const geoToUse = await ensureOperatorReceiptGeo(pendingLoadData.lastLoadA || capturedDriverData?.last_load_a);
      formData = new FormData();
      formData.append('time_b', new Date().toISOString());
      formData.append('geo_b', geoToUse);
      formData.append('photo_b', blob, 'transport.jpg');

      await putLoadMarkB(capturedLoadId, formData);
      onSubmitSuccess();
    } catch (e) {
      const uncertain = formData && is504(e);
      const message = uncertain ? tr("Ответ сервера не получен. Проверьте очередь перед повторной отправкой. Фото сохранено в этом окне.") : getOperatorReceiptError(e);
      if (isTopBaseOperator) setTopBaseConfirmError(message);
      else if (isChppOperator) setChppConfirmError(message);
      else if (isPrivatePointOperator) setPrivatePointConfirmError(message);
      setNotify({ isOpen: true, message, type: uncertain || e instanceof OperatorReceiptError ? 'warning' : 'error' });
      if (uncertain) setRefresh(value => !value);
      setScanningStep('review');
    } finally {
      operatorReceiptPending.current = false;
    }
  }, [photoBlob, camera.videoRef, camera.canvasRef, pendingLoadData, isTopBaseOperator, isChppOperator, isPrivatePointOperator, ensureOperatorReceiptGeo, showSuccess, resetAllOperatorState, is504]);

  const retakePhoto = useCallback(() => {
    setCapturedPhoto(null);
    setPhotoBlob(null);
    setScanningStep('photo');
  }, []);

  // ─── Guard: handleAllow ─────────────────────────────────
  const handleAllow = useCallback(async (autoSubmit = false) => {
    if (!driverUid) return;

    let geoToUse;
    try {
      geoToUse = await ensureGeo();
    } catch (err) {
      setAllowBtnError(tr("Геолокация обязательна для действия! Включите геолокацию и попробуйте снова."));
      return;
    }

    setAllowBtnLoading(true);
    setAllowBtnError(null);
    setGuardSuccess(false);
    try {
      await postLoadByDriverUid(driverUid, { time_a: new Date().toISOString(), geo_a: geoToUse });
      setNotify({ isOpen: true, message: tr("Готово"), type: 'success' });
      setRefresh(r => !r);
      setGuardSuccess(true);

      if (autoSubmit) {
        const currentDriverData = driverData;
        setDriverData(null);
        showSuccess(currentDriverData, 'entry');
      } else {
        setDriverData(null);
      }
    } catch (e) {
      const message = getErpErrorMessage(e, tr("Ошибка отправки"));
      setAllowBtnError(message);
      setNotify({ isOpen: true, message, type: 'warning' });
    } finally {
      setAllowBtnLoading(false);
    }
  }, [driverUid, ensureGeo, driverData, showSuccess]);

  // ─── Operator confirmations (unified) ───────────────────
  const handleOperatorConfirm = useCallback(async (lastLoadA, setLoading, setError, setSuccess, setDriver, setLastLoadState, autoSubmit = false) => {
    if (!lastLoadA?.id || operatorReceiptPending.current) return;
    operatorReceiptPending.current = true;
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const geoToUse = await ensureOperatorReceiptGeo(lastLoadA);
      await postLoadMarkBGuard(lastLoadA.id, { time_b: new Date().toISOString(), geo_b: geoToUse });
      setNotify({ isOpen: true, message: tr("Получение подтверждено"), type: 'success' });
      setSuccess(true);
      setRefresh(r => !r);
      setDriver(null);
      setLastLoadState(null);
    } catch (e) {
      setError(getOperatorReceiptError(e));
    } finally {
      operatorReceiptPending.current = false;
      setLoading(false);
    }
  }, [ensureOperatorReceiptGeo]);

  const handleConfirmTopBase = useCallback((auto) =>
    handleOperatorConfirm(topBaseLastLoadA, setTopBaseConfirmLoading, setTopBaseConfirmError, setTopBaseSuccess, setTopBaseScannedDriver, setTopBaseLastLoadA, auto),
    [topBaseLastLoadA, handleOperatorConfirm]
  );
  const handleConfirmChpp = useCallback((auto) =>
    handleOperatorConfirm(chppLastLoadA, setChppConfirmLoading, setChppConfirmError, setChppSuccess, setChppScannedDriver, setChppLastLoadA, auto),
    [chppLastLoadA, handleOperatorConfirm]
  );
  const handleConfirmPrivatePoint = useCallback((auto) =>
    handleOperatorConfirm(privatePointLastLoadA, setPrivatePointConfirmLoading, setPrivatePointConfirmError, setPrivatePointSuccess, setPrivatePointScannedDriver, setPrivatePointLastLoadA, auto),
    [privatePointLastLoadA, handleOperatorConfirm]
  );

  // ─── Operator modal: QR scanned handler ────────────────
  const handleOperatorModalQrScanned = useCallback(async (uid) => {
    try {
      const res = await getDriverLastLoadA(uid);
      const data = res.data;
      const lastLoadA = data?.last_load_a;
      const lastLoadB = data?.last_load_b;
      const baseName = data?.top_base_name || data?.chpp_name || data?.private_point_name || '-';

      if (lastLoadA) {
        return { driverData: data, lastLoadA };
      }

      // Нет активной погрузки
      if (lastLoadB?.time_b) {
        const minutesSince = moment().diff(moment(lastLoadB.time_b, 'DD.MM.YYYY HH:mm'), 'minutes');
        if (minutesSince >= 60) {
          return { error: tr("Нет активной погрузки для водителя (база: {{value0}})", { value0: baseName }) };
        }
        return { warning: tr("Водитель уже был отмечен (база: {{value0}}). Нет активной погрузки.", { value0: baseName }) };
      }
      if (lastLoadB && !lastLoadB.time_b) {
        return { warning: tr("Водитель уже был отмечен (база: {{value0}}). Нет активной погрузки.", { value0: baseName }) };
      }
      return { error: tr("Нет активной погрузки для данного водителя (база: {{value0}})", { value0: baseName }) };
    } catch (e) {
      throw new Error(tr("Ошибка загрузки данных водителя"));
    }
  }, []);

  // ─── Operator modal: confirm (photo + geo → backend) ───
  const handleOperatorModalConfirm = useCallback(async ({ loadId, lastLoadA, driverData: drvData, photoBlob, geo }) => {
    assertOperatorReceiptLocation(geo, lastLoadA || drvData?.last_load_a);
    const formData = new FormData();
    formData.append('time_b', new Date().toISOString());
    formData.append('geo_b', geo || '');
    formData.append('photo_b', photoBlob, 'transport.jpg');

    const doSend = () => putLoadMarkB(loadId, formData);

    try {
      await doSend();
    } catch (e) {
      if (is504(e)) {
        setRefresh(value => !value);
        throw new OperatorReceiptError(tr("Ответ сервера не получен. Проверьте очередь перед повторной отправкой. Фото сохранено."));
      }
      throw e;
    }

    setNotify({ isOpen: true, message: tr("Готово!"), type: 'success' });
    setRefresh(r => !r);
    if (isTopBaseOperator) setTopBaseSuccess(true);
    else if (isChppOperator) setChppSuccess(true);
    else if (isPrivatePointOperator) setPrivatePointSuccess(true);
    showSuccess(drvData, 'exit');
    setShowOperatorModal(false);
  }, [is504, isTopBaseOperator, isChppOperator, isPrivatePointOperator, showSuccess]);

  // ─── Auto-submit for guard ──────────────────────────────
  useEffect(() => {
    if (isGuard && driverData && !loadingDriver && !errorDriver) {
      if (driverData.status === 'Заблокирован' || driverData.status === 'Не активен' || driverData.status === 'В пути') {
        setErrorModalData({
          fio: driverData.employee_name || '-',
          phone: driverData.employee_phone || '-',
          vehicleNumber: driverData.vehicle_number || '-',
          base: driverData.top_base_name || '-',
          reason: driverData.status === 'Заблокирован' ? tr("ВОДИТЕЛЬ ЗАБЛОКИРОВАН") :
                  driverData.status === 'Не активен' ? tr("ВОДИТЕЛЬ НЕ АКТИВЕН") :
                  tr("ВОДИТЕЛЬ В ПУТИ (НЕ ДОСТАВИЛ ПРЕЖНЮЮ ПОГРУЗКУ)"),
        });
        setShowErrorModal(true);
      } else {
        handleAllow(true);
      }
    }
  }, [driverData, loadingDriver, errorDriver, isGuard, handleAllow]);

  // ─── Auto-submit for operators (in range) ───────────────
  useEffect(() => {
    if (!pendingLoadData && isTopBaseOperator && topBaseScannedDriver && topBaseLastLoadA && !topBaseScanLoading && !topBaseScanError && !topBaseConfirmLoading && !topBaseConfirmError) {
      const { isInRange } = checkOperatorReceiptLocation(userGeoLocation, topBaseLastLoadA);
      if (isInRange) handleConfirmTopBase(true);
    }
  }, [pendingLoadData, topBaseScannedDriver, topBaseLastLoadA, topBaseScanLoading, topBaseScanError, topBaseConfirmLoading, topBaseConfirmError, isTopBaseOperator, userGeoLocation, handleConfirmTopBase]);

  useEffect(() => {
    if (!pendingLoadData && isChppOperator && chppScannedDriver && chppLastLoadA && !chppScanLoading && !chppScanError && !chppConfirmLoading && !chppConfirmError && chppScannedDriver.status !== 'Не активен') {
      const { isInRange } = checkOperatorReceiptLocation(userGeoLocation, chppLastLoadA);
      if (isInRange) handleConfirmChpp(true);
    }
  }, [pendingLoadData, chppScannedDriver, chppLastLoadA, chppScanLoading, chppScanError, chppConfirmLoading, chppConfirmError, isChppOperator, userGeoLocation, handleConfirmChpp]);

  useEffect(() => {
    if (!pendingLoadData && isPrivatePointOperator && privatePointScannedDriver && privatePointLastLoadA && !privatePointScanLoading && !privatePointScanError && !privatePointConfirmLoading && !privatePointConfirmError && privatePointScannedDriver.status !== 'Не активен') {
      const { isInRange } = checkOperatorReceiptLocation(userGeoLocation, privatePointLastLoadA);
      if (isInRange) handleConfirmPrivatePoint(true);
    }
  }, [pendingLoadData, privatePointScannedDriver, privatePointLastLoadA, privatePointScanLoading, privatePointScanError, privatePointConfirmLoading, privatePointConfirmError, isPrivatePointOperator, userGeoLocation, handleConfirmPrivatePoint]);

  // ─── Out-of-range warning for operators ──────────────────
  useEffect(() => {
    const checkAndWarn = (lastLoad, requirementText) => {
      const { isInRange, reason, message, distanceInfo, distances } = checkOperatorReceiptLocation(userGeoLocation, lastLoad);
      if (!isInRange) {
        setWarningModalData({
          title: reason === 'outside' ? tr('Оператор вне зоны действия') : tr('Внимание'),
          message,
          distance: distanceInfo,
          distances,
          requirement: requirementText,
        });
        setShowWarningModal(true);
      }
    };

    if (isTopBaseOperator && topBaseScannedDriver && topBaseLastLoadA && !topBaseScanLoading && !topBaseScanError) {
      checkAndWarn(topBaseLastLoadA, tr("Необходимо находиться в радиусе 200 метров от одного из филиалов базы."));
    }
    if (isChppOperator && chppScannedDriver && chppLastLoadA && !chppScanLoading && !chppScanError && chppScannedDriver.status !== 'Не активен') {
      checkAndWarn(chppLastLoadA, tr("Необходимо находиться в радиусе 200 метров от одного из филиалов ТЭЦ."));
    }
    if (isPrivatePointOperator && privatePointScannedDriver && privatePointLastLoadA && !privatePointScanLoading && !privatePointScanError && privatePointScannedDriver.status !== 'Не активен') {
      checkAndWarn(privatePointLastLoadA, tr("Необходимо находиться в радиусе 200 метров от одного из филиалов соц.объекта или МСУ."));
    }
  }, [
    topBaseScannedDriver, topBaseLastLoadA, topBaseScanLoading, topBaseScanError, isTopBaseOperator,
    chppScannedDriver, chppLastLoadA, chppScanLoading, chppScanError, isChppOperator,
    privatePointScannedDriver, privatePointLastLoadA, privatePointScanLoading, privatePointScanError, isPrivatePointOperator,
    userGeoLocation,
  ]);

  // ─── Open scanner helper ────────────────────────────────
  const openScanner = useCallback((resetSuccessFn) => {
    resetSuccessFn(false);
    setShowQr(true);
  }, []);

  // ─── Render ─────────────────────────────────────────────
  return (
    <Layout>
      <div className={styles.workspace}>
        {/* Role check loading — nothing renders until we know who the user is */}
        {tr(roleLoading && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', flexDirection: 'column', gap: 16, color: "var(--theme-text-muted, #6b7280)" }}>
            <div style={{ width: 40, height: 40, border: "4px solid var(--theme-border, #e5e7eb)", borderTop: "4px solid var(--theme-border-strong, #374151)", borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <span style={{ fontSize: 14 }}>{tr("Загрузка...")}</span>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        ))}

        {tr(!roleLoading && roleError && <div className={styles.statePanel} role="alert"><p>{tr(roleError)}</p><button onClick={retryRoles}>{tr("Повторить загрузку")}</button></div>)}

        {/* Driver Cabinet - shown for self-registered drivers */}
        {tr(!roleLoading && isDriverChecked && !isGuard && !isTopBaseOperator && !isChppOperator && !isPrivatePointOperator && (
          <DriverCabinet user={user} />
        ))}

        {tr(!roleLoading && !roleError && !isDriverChecked && (
        <div className="qr-container">
          <div>
            {/* Header */}
            <header className={styles.pageHeader}>
              <div className={styles.pageHeading}>
                <span className={styles.pageHeaderIcon} aria-hidden="true">
                  {isGuard ? <ShieldCheck size={22} /> : <Truck size={22} />}
                </span>
                <div className={styles.pageHeaderText}>
                  <h1>{isGuard ? tr("Рабочее место охраны") : operational ? tr("Рабочее место оператора") : tr("Контроль логистики")}</h1>
                  <p>{isGuard ? tr("Кара-Кече · въезд, погрузка и выезд") : operational ? tr("Приём перевозок и контроль назначенной базы") : tr("Погрузки, перевозки и история операций")}</p>
                </div>
              </div>
              <button type="button" onClick={() => setRefresh(value => !value)} className={styles.pageRefresh} aria-label={tr("Обновить")} title={tr("Обновить списки и счётчики")}>
                <RefreshCw size={16} aria-hidden="true" />
                <span>{tr("Обновить")}</span>
              </button>
            </header>
            {/* <div className={styles.refreshHint}>При возникновении сбоев попробуйте обновить страницу</div> */}

            {/* Legend
            <div className={styles.legend}>
              <div className={styles.legendItem}>
                <div aria-hidden className={`${styles.legendBox} ${styles.legendBoxDanger}`} />
                <div className={styles.legendText}>Отметка была совершена вне зоны диапазона действия</div>
              </div>
              <div className={styles.legendItem}>
                <div aria-hidden className={`${styles.legendBox} ${styles.legendBoxDuration}`} />
                <div className={styles.legendText}>Длительность перевозки превышает норму</div>
              </div>
            </div> */}

            {/* Scan buttons */}
            {tr((isGuard || isTopBaseOperator || isChppOperator || isPrivatePointOperator) && (
            <div style={{ padding: '0px 0', marginBottom: '0px' }}>
              {tr(roleError && <div style={{ color: "var(--theme-danger-text, red)" }}>{tr(roleError)}</div>)}
              <ScanDataContext.Provider value={{ scanData, setScanData }}>
                <div className="combined-app">
                  {tr(isGuard && (
                    <ScanButton
                      label={tr("Сканировать QR")}
                      success={guardSuccess}
                      onScan={() => openScanner(setGuardSuccess)}
                      onNotify={setNotify}
                    />
                  ))}
                  {tr(isTopBaseOperator && (
                    <ScanButton
                      label={tr("Сканировать")}
                      success={topBaseSuccess}
                      onScan={() => { setTopBaseSuccess(false); setShowOperatorModal(true); }}
                      onNotify={setNotify}
                    />
                  ))}
                  {tr(isChppOperator && (
                    <ScanButton
                      label={tr("Сканировать")}
                      success={chppSuccess}
                      onScan={() => { setChppSuccess(false); setShowOperatorModal(true); }}
                      onNotify={setNotify}
                    />
                  ))}
                  {tr(isPrivatePointOperator && (
                    <ScanButton
                      label={tr("Сканировать")}
                      success={privatePointSuccess}
                      onScan={() => { setPrivatePointSuccess(false); setShowOperatorModal(true); }}
                      onNotify={setNotify}
                    />
                  ))}
                </div>
              </ScanDataContext.Provider>
            </div>
            ))}

            {/* Last 3 Marks */}
            {/* <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '16px', marginTop: '24px', color: '#2c3e50' }}>Последние отметки (В погрузке)</h2>
            <Last3Marks loads={loads.last3Marks} loading={loads.last3Loading} /> */}

            {/* Base requests panel for operators */}
            {tr((isTopBaseOperator || isChppOperator || isPrivatePointOperator) && (
              <Link to="/requests/" className={styles.requestsLink}>{tr("Заявки на базы и подтверждения →")}</Link>
            ))}

            {/* Section tab nav */}
            <div className={styles.rcNavBar}>
              {[
                { key: 'entered', icon: <ArrowDownToLine size={17} />, label: "Въезд", count: loads.tabCounts.entered },
                { key: 'queued', icon: <Truck size={17} />, label: "В очереди", count: loads.tabCounts.queued },
                { key: 'repair', icon: <ShieldCheck size={17} />, label: "На ремонте", count: loads.tabCounts.repair },
                ...(hasAccess(user, 'loads') ? [{ key: 'lanes', icon: <Truck size={17} />, label: "Полосы" }] : []),
                { key: 'inLoading',  icon: <ArrowDownToLine size={17} />,  label: "В погрузке",  count: loads.tabCounts.in_loading },
                { key: 'inRoute',    icon: <Truck size={17} />,  label: "В пути",      count: loads.tabCounts.in_route },
                { key: 'history',    icon: <History size={17} />,  label: "История",     count: loads.tabCounts.completed },
                { key: 'analytics',  icon: <BarChart3 size={17} />,  label: "Аналитика" },
                ...((isTopBaseOperator || isChppOperator || isPrivatePointOperator)
                  ? [{ key: 'drivers', icon: <Users size={17} />, label: "Наши водители", count: loads.tabCounts.drivers }]
                  : []),
              ].map(({ key, icon, label, count }) => {
                const isActive = activeTab === key;
                return (
                  <button
                    key={key}
                    className={`${styles.rcNavTab} ${isActive ? styles.rcNavTabActive : ''}`}
                    onClick={() => selectTab(key)}
                    aria-pressed={isActive}
                  >
                    <span className={styles.rcNavTabIcon}>{icon}</span>
                    <span className={styles.rcNavTabLabel}>{tr(label)}</span>
                    {tr(count > 0 && (
                      <span className={isActive ? styles.rcNavTabBadge : styles.rcNavTabBadgeInactive}>
                        {tr(count)}
                      </span>
                    ))}
                  </button>
                );
              })}
            </div>

            {tr(['entered', 'queued', 'repair'].includes(activeTab) && <IntegrationsPanel key={activeTab} resource="loads" stage={activeTab} refresh={refresh} />)}
            {tr(activeTab === 'lanes' && <IntegrationsPanel resource="queues" refresh={refresh} />)}
            {tr(loads.queueError && <div className={styles.statePanel} role="alert">{tr(loads.queueError)}<button onClick={() => setRefresh(value => !value)}>{tr("Повторить")}</button></div>)}

            {tr(isQueueSearchActive(loads, showInLoading, showInRoute, showHistory) && <p className={styles.queueFilterHint}>{tr("Показаны результаты поиска. Счётчик вкладки показывает все перевозки этого этапа.")}</p>)}

            {/* In-loading table collapsible */}
            {tr(showInLoading && (
              <InLoadingTableView
                  loading={loads.queueLoading}
                  pending={loads.queuePending}
                  error={loads.queueError}
                  inLoadingLoads={loads.inLoadingLoads}
                  inLoadingCount={loads.inLoadingCount}
                  inLoadingNext={loads.inLoadingNext}
                  inLoadingPrev={loads.inLoadingPrev}
                  inLoadingPage={loads.inLoadingDataPage}
                  setInLoadingPage={loads.setInLoadingPage}
                  inLoadingSearch={loads.inLoadingSearch}
                  setInLoadingSearch={loads.setInLoadingSearch}
                  nowTime={loads.nowTime}
                  user={user}
              />
            ))}

            {/* Scanner overlay */}
            {tr(showQr && (
              <ScannerOverlay
                isGuard={isGuard}
                scanningStep={scanningStep}
                capturedPhoto={capturedPhoto}
                videoRef={camera.videoRef}
                canvasRef={camera.canvasRef}
                switchCamera={camera.switchCamera}
                selectCamera={camera.selectCamera}
                availableCameras={camera.availableCameras}
                currentCameraInfo={camera.currentCameraInfo}
                isCameraLoading={camera.isCameraLoading}
                capturePhoto={capturePhoto}
                retakePhoto={retakePhoto}
                handleSendPhoto={() => capturePhoto(true)}
                handleCloseScanner={handleCloseScanner}
              />
            ))}

            {/* Guard driver info */}
            {tr(isGuard && (
              <>
                {tr(loadingDriver && <div>{tr("Загрузка данных водителя...")}</div>)}
                {tr(errorDriver && <div role="alert" className={styles.statePanel}>{tr(errorDriver)}</div>)}
                {tr(allowBtnError && <div role="alert" className={styles.statePanel}>{tr(allowBtnError)}</div>)}
              </>
            ))}

            {/* Operator panels */}
            {(topBaseScannedDriver || chppScannedDriver || privatePointScannedDriver) && <button type="button" className={styles.btn_tabel_driver} disabled={operatorGeoChecking || topBaseConfirmLoading || chppConfirmLoading || privatePointConfirmLoading} onClick={recheckOperatorLocation}>
              {operatorGeoChecking ? tr('Проверяем местоположение…') : tr('Проверить местоположение')}
            </button>}
            {tr(isTopBaseOperator && (
              <OperatorDriverPanel
                operatorType="topbase"
                scannedDriver={topBaseScannedDriver}
                lastLoadA={topBaseLastLoadA}
                scanLoading={topBaseScanLoading}
                scanError={topBaseScanError}
                confirmLoading={topBaseConfirmLoading}
                confirmError={topBaseConfirmError}
                userGeoLocation={userGeoLocation}
                operatorBranches={topBaseLastLoadA?.geo_base || []}
                onConfirm={handleConfirmTopBase}
              />
            ))}
            {tr(isChppOperator && (
              <OperatorDriverPanel
                operatorType="chpp"
                scannedDriver={chppScannedDriver}
                lastLoadA={chppLastLoadA}
                scanLoading={chppScanLoading}
                scanError={chppScanError}
                confirmLoading={chppConfirmLoading}
                confirmError={chppConfirmError}
                userGeoLocation={userGeoLocation}
                operatorBranches={chppLastLoadA?.geo_base || []}
                onConfirm={handleConfirmChpp}
              />
            ))}
            {tr(isPrivatePointOperator && (
              <OperatorDriverPanel
                operatorType="privatepoint"
                scannedDriver={privatePointScannedDriver}
                lastLoadA={privatePointLastLoadA}
                scanLoading={privatePointScanLoading}
                scanError={privatePointScanError}
                confirmLoading={privatePointConfirmLoading}
                confirmError={privatePointConfirmError}
                userGeoLocation={userGeoLocation}
                operatorBranches={privatePointLastLoadA?.geo_base || []}
                onConfirm={handleConfirmPrivatePoint}
              />
            ))}

            {/* In-route table collapsible */}
            {tr(showInRoute && (
              <InRouteTableView
                  loading={loads.queueLoading}
                  pending={loads.queuePending}
                  error={loads.queueError}
                  inRouteLoads={loads.inRouteLoads}
                  inRouteCount={loads.inRouteCount}
                  inRouteNext={loads.inRouteNext}
                  inRoutePrev={loads.inRoutePrev}
                  inRoutePage={loads.inRouteDataPage}
                  setInRoutePage={loads.setInRoutePage}
                  inRouteSearch={loads.inRouteSearch}
                  setInRouteSearch={loads.setInRouteSearch}
                  nowTime={loads.nowTime}
                  user={user}
              />
            ))}

            {/* Completed loads collapsible */}
            {tr(showHistory && (
              <CompletedLoadsTableView
                  loading={loads.queueLoading}
                  pending={loads.queuePending}
                  error={loads.queueError}
                  completedLoads={loads.completedLoads}
                  completedCount={loads.completedCount}
                  completedNext={loads.completedNext}
                  completedPrev={loads.completedPrev}
                  completedPage={loads.completedDataPage}
                  setCompletedPage={loads.setCompletedPage}
                  completedSearch={loads.completedSearch}
                  setCompletedSearch={loads.setCompletedSearch}                  user={user}                
                />
            ))}
          </div>

          {/* Analytics collapsible */}
          <div style={{ width: '100%' }}>
            {tr(showAnalytics && (
              <AnalyticsSection
                statsLoading={loads.statsLoading}
                canReadBlocked={loads.canReadBlocked}
                statsData={loads.statsData}
                blockedDrivers={loads.blockedDrivers}
                blockedDriversLoading={loads.blockedDriversLoading}
                blockedDriversError={loads.blockedDriversError}
                blockedDriversLoaded={loads.blockedDriversLoaded}
                blockedDriversPage={loads.blockedDriversPage}
                blockedDriversDataPage={loads.blockedDriversDataPage}
                setBlockedDriversPage={loads.setBlockedDriversPage}
                blockedDriversCount={loads.blockedDriversCount}
                blockedPageSize={loads.BLOCKED_PAGE_SIZE}
              />
            ))}
          </div>

          {/* Our drivers panel — shown via tab */}
          {tr(activeTab === 'drivers' && (isTopBaseOperator || isChppOperator || isPrivatePointOperator) && (
            <BaseDriversPanel />
          ))}
        </div>
        ))}
      </div>

      <Notification notify={notify} setNotify={setNotify} />

      {/* History modal */}
      {tr(loads.showHistoryModal && (
        <HistoryModalView
          arrivedBLoads={loads.arrivedBLoads}
          arrivedBCount={loads.arrivedBCount}
          arrivedBNext={loads.arrivedBNext}
          arrivedBPrev={loads.arrivedBPrev}
          arrivedBPage={loads.arrivedBPage}
          setArrivedBPage={loads.setArrivedBPage}
          arrivedBSearch={loads.arrivedBSearch}
          setArrivedBSearch={loads.setArrivedBSearch}
          historyLoading={loads.historyLoading}
          historyTableLoading={loads.historyTableLoading}
          loadHistory={loads.loadHistory}
          onClose={() => loads.setShowHistoryModal(false)}
          user={user}
        />
      ))}

      {/* Status modals */}
      <StatusModals
        showSuccessModal={showSuccessModal}
        successModalData={successModalData}
        onCloseSuccess={() => { setShowSuccessModal(false); setSuccessModalData(null); }}
        showErrorModal={showErrorModal}
        errorModalData={errorModalData}
        onCloseError={() => { setShowErrorModal(false); setErrorModalData(null); setDriverData(null); }}
        showWarningModal={showWarningModal}
        warningModalData={warningModalData && { ...warningModalData, distance: warningModalData.distances?.length ? formatBranchDistances(warningModalData.distances) : warningModalData.distance }}
        onCloseWarning={() => { setShowWarningModal(false); setWarningModalData(null); }}
      />

      {/* Operator scan modal (QR + фото транспорта) */}
      {tr(showOperatorModal && (isTopBaseOperator || isChppOperator || isPrivatePointOperator) && (
        <OperatorScanModal
          onClose={() => setShowOperatorModal(false)}
          onQRScanned={handleOperatorModalQrScanned}
          onConfirm={handleOperatorModalConfirm}
          ensureGeo={ensureGeo}
        />
      ))}
    </Layout>
  );
};

export default ReportCard;
